import { useState, useRef, useCallback } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';

const HINTS = [
  { keys: 'Dbl-click canvas', action: 'Create state' },
  { keys: 'Shift-drag → state', action: 'Create transition' },
  { keys: 'Shift-click state', action: 'Create loop' },
  { keys: 'Single-click', action: 'Select' },
  { keys: 'Dbl-click state', action: 'Rename' },
  { keys: 'Dbl-click transition', action: 'Edit label / rules' },
  { keys: 'Right-click', action: 'Context menu' },
  { keys: 'Del / Bksp', action: 'Remove selected' },
  { keys: 'Ctrl+Z / Ctrl+Y', action: 'Undo / Redo' },
  { keys: '\\l  \\e  \\0', action: 'λ  ε  ∅' },
  { keys: '^n  ^{2n}', action: 'ⁿ  ²ⁿ superscripts' },
  { keys: '0..9  a..z', action: 'Range shorthand' },
  { keys: 'Scroll / Pinch', action: 'Zoom' },
  { keys: 'Drag canvas', action: 'Pan' },
];

export function HintsPanel({ visible, onToggleVisible }) {
  const [expanded, setExpanded] = useState(true);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const panelRef = useRef(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleDragStart = useCallback((e) => {
    isDragging.current = true;
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    const onMove = (me) => {
      if (!isDragging.current) return;
      const parent = panelRef.current?.parentElement?.getBoundingClientRect();
      if (!parent) return;
      setPosition({
        x: Math.max(0, Math.min(parent.width - 200, me.clientX - parent.left - dragOffset.current.x)),
        y: Math.max(0, Math.min(parent.height - 40, me.clientY - parent.top - dragOffset.current.y)),
      });
    };
    const onUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  if (!visible) return null;

  return (
    <div
      ref={panelRef}
      className="absolute z-20 bg-card/95 backdrop-blur-sm border border-border rounded-lg shadow-lg"
      style={{ left: position.x, top: position.y, width: 280 }}
      data-testid="hints-panel"
    >
      <div
        className="flex items-center justify-between px-3 py-1.5 border-b border-border/30 cursor-move select-none"
        onMouseDown={handleDragStart}
      >
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Shortcuts</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setExpanded(!expanded)} className="p-0.5 hover:bg-muted rounded transition-colors">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <button onClick={onToggleVisible} className="p-0.5 hover:bg-muted rounded transition-colors">
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-3 py-2">
          <div className="grid gap-y-0.5" style={{ gridTemplateColumns: 'auto 1fr' }}>
            {HINTS.map((h, i) => (
              <div key={i} className="contents text-[11px]">
                <kbd className="font-mono text-[10px] text-muted-foreground whitespace-nowrap pr-3 py-[1px]">{h.keys}</kbd>
                <span className="text-muted-foreground/70 whitespace-nowrap py-[1px]">{h.action}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
